import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, Users, PartyPopper, Lock } from 'lucide-react';
import { tailorMadeCategories } from '../data/mock';
import { Button } from './ui/button';

const iconMap = {
  Heart: Heart,
  Users: Users,
  PartyPopper: PartyPopper,
  Lock: Lock,
};

export const TailorMadeSection = ({ onNavigate }) => {
  const { t } = useTranslation();

  return (
    <section className="py-24 bg-white" id="tailor-made">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl sm:text-5xl font-serif text-stone-800 mb-4">
            {t('tailorMade.title')}
          </h2>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto">
            {t('tailorMade.subtitle')}
          </p>
        </motion.div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {tailorMadeCategories.map((category, index) => {
            const IconComponent = iconMap[category.icon] || Heart;
            
            return (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={() => onNavigate('contact')}
              >
                <div className="relative h-80 rounded-2xl overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-stone-900/90 via-stone-900/40 to-transparent" />
                  
                  {/* Content Overlay */}
                  <div className="absolute inset-0 p-8 flex flex-col justify-end">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-amber-500/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <IconComponent className="w-5 h-5 text-amber-400" />
                      </div>
                      <h3 className="text-2xl font-serif text-white">
                        {category.title}
                      </h3>
                    </div>
                    <p className="text-stone-300 mb-4">
                      {category.description}
                    </p>
                    <Button
                      variant="ghost"
                      className="p-0 h-auto text-amber-400 hover:text-amber-300 hover:bg-transparent group/btn w-fit"
                    >
                      {t('tailorMade.planJourney')}
                      <ArrowRight className="ml-2 w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
